import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { bookingQuery, type BookingDetail } from '@/lib/bookings';
import { bookingQuoteQuery, completePriceMinor } from '@/lib/bookingQuote';
import { paymentScheduleQuery } from '@/lib/payments';
import { useChangeCheckout, changeCheckoutErrorCode, checkoutMoneySummary } from '@/lib/changeCheckout';
import { errorMessage } from '@/lib/errors';
import { todayYMD } from '@/lib/calendar';
import { useToast } from '@/components/ToastProvider';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import { inputCls, labelCls } from './formStyles';

export interface ChangeCheckoutPanelProps {
  booking: BookingDetail;
  onClose: () => void;
}

/**
 * «Change checkout» panel (spec §4.8): move the checkout date of a booking
 * WE own (extension or early departure, past dates allowed — R1), price the
 * extension when one applies, and submit
 * `POST .../change-checkout` optimistic on `booking.version`.
 *
 * Money already received is untouched here — the operator edits the payment
 * schedule separately (PaymentsCard); this panel only shows where the new
 * total leaves the balance.
 */
export function ChangeCheckoutPanel({ booking, onClose }: ChangeCheckoutPanelProps) {
  const qc = useQueryClient();
  const { toast } = useToast();

  const [checkout, setCheckout] = useState(booking.checkout_date);
  const [totalMajor, setTotalMajor] = useState(() => (booking.total_amount_minor / 100).toFixed(2));
  // Autofill guard (same pattern as NewBookingForm): the field starts holding
  // the current total, itself a value we "wrote"; a re-quote may overwrite it
  // only while it still holds exactly what we last wrote. The moment the
  // operator types something else, every later quote leaves it alone.
  const autoFilledRef = useRef<string | null>(totalMajor);
  const [allowOverbook, setAllowOverbook] = useState(false);
  const [showOverbook, setShowOverbook] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  // Captured synchronously in handleSubmit before the async mutation runs —
  // same reasoning as NewBookingForm's sendOverbookRef (React 18 batching).
  const sendOverbookRef = useRef(false);

  const scheduleQ = useQuery(paymentScheduleQuery(booking.id));
  const receivedMinor = scheduleQ.data?.received_total_minor ?? 0;

  // A price hint only makes sense for an extension of a stay whose old
  // checkout has not already passed — the quote endpoint refuses a past
  // checkin, so a checkout already behind us always falls back to a manual
  // total (spec §4.8). Shortening never quotes: there is nothing to add.
  const isExtension = checkout > booking.checkout_date;
  const oldCheckoutNotPast = booking.checkout_date >= todayYMD();
  const quoteQ = useQuery({
    ...bookingQuoteQuery(booking.apartment_id, booking.checkout_date, checkout, {
      adults: booking.adults,
      children: booking.children,
      infants: booking.infants,
    }),
    enabled: isExtension && oldCheckoutNotPast,
  });

  useEffect(() => {
    // A quote that no longer applies (disabled, or the operator shortened
    // back past it) must fall back to the PLAIN current total, not leave the
    // previous extension's quoted total sitting in the field — a shorten
    // right after an extend quote used to submit a total priced for a
    // different, longer stay. The guard still decides whether we may write
    // at all: a hand-typed value survives this exactly like it survives a
    // real quote.
    const quoteMinor = completePriceMinor(quoteQ.data);
    const next =
      quoteMinor == null
        ? (booking.total_amount_minor / 100).toFixed(2)
        : ((booking.total_amount_minor + quoteMinor) / 100).toFixed(2);
    setTotalMajor((cur) => {
      if (cur !== '' && cur !== autoFilledRef.current) return cur; // the operator's number
      autoFilledRef.current = next;
      return next;
    });
  }, [quoteQ.data, booking.total_amount_minor]);

  // Overbooking is offered once something actually blocks the extension: the
  // quote reporting blocked nights, or a 409 no_availability the quote missed.
  const overbookVisible = showOverbook || (quoteQ.data?.blocked_nights?.length ?? 0) > 0;

  const mut = useChangeCheckout(booking.id);

  const parsedTotal = parseFloat(totalMajor);
  const totalMinorForSummary = Number.isFinite(parsedTotal) ? Math.round(parsedTotal * 100) : 0;

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);

    if (!checkout) {
      setFormError('Pick a checkout date.');
      return;
    }
    const minor = Math.round(parsedTotal * 100);
    if (!Number.isFinite(minor) || minor < 0) {
      setFormError('Enter a valid total amount.');
      return;
    }

    sendOverbookRef.current = allowOverbook && overbookVisible;

    mut.mutate(
      {
        expectedVersion: booking.version,
        checkout,
        totalMinor: minor,
        allowOverbook: sendOverbookRef.current,
      },
      {
        onSuccess: () => {
          toast({ kind: 'success', title: 'Checkout changed' });
          onClose();
        },
        onError: (err) => {
          const code = changeCheckoutErrorCode(err);
          if (code === 'stale') {
            toast({ kind: 'error', title: 'Booking changed — reload' });
            void qc.invalidateQueries({ queryKey: bookingQuery(booking.id).queryKey });
            return;
          }
          if (code === 'no_availability') {
            setShowOverbook(true);
            setFormError('No availability for these dates.');
            return;
          }
          setFormError(errorMessage(err));
        },
      },
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div>
        <p className={labelCls}>Check-in</p>
        <p className="text-sm text-foreground">{booking.checkin_date}</p>
      </div>

      <div>
        <p className={labelCls}>Check-out *</p>
        <DatePicker
          value={checkout}
          onChange={(d) => setCheckout(d ?? '')}
          placeholder="Pick checkout"
        />
      </div>

      <div>
        <label htmlFor="cc-total" className={labelCls}>
          Total *
        </label>
        <input
          id="cc-total"
          type="number"
          required
          min={0}
          step="0.01"
          className={inputCls}
          value={totalMajor}
          onChange={(e) => setTotalMajor(e.target.value)}
        />
      </div>

      <p className="text-sm text-muted">
        {checkoutMoneySummary({
          receivedMinor,
          newTotalMinor: totalMinorForSummary,
          currency: booking.currency,
        })}
      </p>

      {overbookVisible && (
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={allowOverbook}
            onChange={(e) => setAllowOverbook(e.target.checked)}
          />
          Allow overbooking
        </label>
      )}

      {formError && (
        <p role="alert" className="text-sm text-destructive">
          {formError}
        </p>
      )}

      <div className="flex gap-2 pt-2">
        <Button type="submit" variant="primary" loading={mut.isPending} disabled={mut.isPending}>
          {mut.isPending ? 'Saving…' : 'Save'}
        </Button>
        <Button type="button" variant="secondary" onClick={onClose} disabled={mut.isPending}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
